import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';
import { TravelReason } from 'models/travelReseller';
import RejectedlReasonEmptyList from './components/RejectedReasonEmptyList';
import { List, Container } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import { deleteRejectedReason } from 'services/resellerTravel';
import useToast from 'hooks/useToast';
import { CloseButton, CloseIcon } from 'components/ModalHeader/styles';

interface Props {
  travelTarget?: string;
  rejectedReasonList: TravelReason[];
  setRejectedReasonList: React.Dispatch<React.SetStateAction<TravelReason[]>>;
  isLoading: boolean;
  tourHasAccess?: boolean;
  tourLoading: boolean;
  ruleSelected?: TravelReason;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (rule: TravelReason) => void;
  onDeleteSuccess?: () => void;
}

const RejectedReasonList = ({ rejectedReasonList, setRejectedReasonList, isLoading, tourLoading, onDeleteSuccess }: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [rejectedResult, setRejectedResult] = useState<TravelReason[]>([]);
  const [rejectedReasonSelected, setRejectedReasonSelected] = useState<TravelReason | undefined>(undefined);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const { t } = useStoreTranslation();
  const rejectedData = { isAddButton: true };
  /* estados globales */
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const handleSelection = (rejectedReasonId: TravelReason) => {
    history.replace(`/corporate/rejectedForm/${rejectedReasonId.id}`);
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setShowSearch(true);
      setRejectedResult(
        rejectedReasonList.filter(
          (name: TravelReason) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else {
      setShowSearch(false);
    }
  };
  const handleDelete = async () => {
    try {
      const data = await deleteRejectedReason(rejectedReasonSelected?.id);
      if (data === 'ok') {
        toast.add({
          type: 'success',
          message: t('resellerForm.delete.success'),
        });
        onDeleteSuccess?.();
      }
    } catch (error) {}
  };

  const handleConfirmDelete = (travel?: TravelReason) => {
    if (rejectedReasonList.length === 1) {
      toast.add({ type: 'danger', message: t('resellerForm.deleteLastReason.error') });
      return;
    }
    setShowConfirmDelete(true);
    setRejectedReasonSelected(travel);
  };

  const renderList = () => (
    <>
      <Search show={rejectedReasonList.length >= 10} onSearch={handleOnSearch} />

      <Mobile>
        {showSearch ? (
          <List>
            <GroupedItem
              quantityText={htmlT(t, 'groupList.searchResults', { count: rejectedResult?.length })}
              isSearch={showSearch}
              items={rejectedResult}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </List>
        ) : (
          <>
            <GroupedList
              isAlphabetic
              isSelectable={false}
              items={rejectedReasonList}
              isSearch={showSearch}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </>
        )}
      </Mobile>

      <Desktop>
        <List>
          {(showSearch ? rejectedResult : rejectedReasonList).map((rejected) => (
            <Item
              key={`user-${rejected.id}`}
              title={rejected.name || ''}
              deleteMode={!rejected.isFromReseller}
              forceEdit={!rejected.isFromReseller}
              disabled={rejected.isFromReseller}
              onDelete={() => handleConfirmDelete(rejected)}
              onClick={() => handleSelection(rejected)}
            />
          ))}
        </List>
        <ConfirmBottomSheet
          show={showConfirmDelete}
          title={t('resellerForm.confirmDelete.title')}
          onClose={() => setShowConfirmDelete(false)}
          content={t('resellerForm.confirmDelete.text')}
          confirmText={t('rule.delete.bottomSheetButton')}
          onConfirm={() => handleDelete()}
        />
      </Desktop>
    </>
  );
  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('groupList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.replace('/corporate/configurations')}>
          <h5>{t('rejectedReason.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader
              title={t('rejectedReason.title')}
              subtitle={t('rejectedReason.description')}
              nameCompany={t('rejectedReason.nameCompany', { company: accountResult.companyName })}
              right={
                <CloseButton onClick={() => history.goBack()}>
                  <CloseIcon />
                </CloseButton>
              }
            />
          </Desktop>

          <Container hasList={rejectedReasonList.length > 0}>
            <Tour
              type="cancelable"
              show={!tourLoading /* && !tourHasAccess */}
              hideArrow
              disableOverlayClose
              disableScrollParentFix
            />
            {rejectedReasonList && rejectedReasonList.length ? renderList() : <RejectedlReasonEmptyList />}
          </Container>
        </>
      )}
      <FloatingButton
        zIndex={2}
        onClick={() => history.replace({ pathname: '/corporate/rejectedForm', state: { rejectedData } })}
        className="target"
      />
    </>
  );
};

export default RejectedReasonList;
